import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import { sendSocket } from "../../websockets/ws";
// test
/**
 * reducers: `myFunc(state, payload)`
 * effects:  `async myFunc(payload, rootState)`
 */
const SubscribeModel = {
  state: {
    isWebsocketReady: false,
    events: [] as Array<any>,
    markets: [] as Array<any>,
    runners: [] as Array<any>,
    reconnecting: false,
  },
  reducers: {
    /**
     * payload: { isReady }
     */
    updateWebsocketState(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return { ...state, isWebsocketReady: payload.isReady };
    },
    /**
     * payload: { reconnecting }
     */
    isReconnecting(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return { ...state, reconnecting: payload.reconnecting };
    },

    /**
     *  update events list with record of subscribed events
     *  in case of resubscription of events, market, runners
     *  after web socket reconncetion after web socket closing
     */
    addEventToEventsList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let eventsList = cloneDeep(state.events);
      let eventMessage = { ...payload };
      eventsList.push(eventMessage.id);
      return { ...state, events: uniq(eventsList) };
    },

    /**
     * remove event from eventMessages list
     */
    removeEventFromEventsList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let eventsList = cloneDeep(state.events);
      let validEventMessageList = eventsList.filter(
        event => event !== payload.id
      );
      return { ...state, events: validEventMessageList };
    },

    /**
     *  update marketsMessage list with event
     */
    addMarketToMarketsList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let marketsList = cloneDeep(state.markets);
      let market = { ...payload };
      marketsList.push(market.id);
      return { ...state, markets: uniq(marketsList) };
    },

    /**
     * remove market from marketMessages list
     */
    removeMarketFromMarketsList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let marketsList = cloneDeep(state.markets);
      let validMarketMessageList = marketsList.filter(
        market => market !== payload.id
      );
      return { ...state, markets: validMarketMessageList };
    },

    /**
     *  update runnersMessage list with event
     */
    addRunnerToRunnersList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let runnersList = cloneDeep(state.runners);
      let runner = { ...payload };
      runnersList.push(runner.id);
      return { ...state, runners: uniq(runnersList) };
    },

    /**
     * remove runner from runners list
     */
    removeRunnerFromRunnersList(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      let runnersList = cloneDeep(state.runners);
      let validRunnerMessageList = runnersList.filter(
        runner => runner !== payload.id
      );
      return { ...state, runners: validRunnerMessageList };
    },

    /**
     * remove all runners from runners list
     */
    removeAllRunnerFromRunnersList(state: Record<string, any>): any {
      return { ...state, runners: [] };
    },
  },
  effects: {
    /**
     * payload: { message }
     */
    async sendMessageSocket(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      sendSocket(payload.message);
    },

    /**
     * payload: { eventId }
     */
    async subscribeEvent(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        const event = {
          type: "event",
          id: payload.eventId,
        };
        const message = {
          interested: [event],
        };
        this.addEventToEventsList(event);
        sendSocket(message);
      }
    },

    /**
     * payload: { eventId }
     */
    async unsubscribeEvent(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        const event = {
          type: "event",
          id: payload.eventId,
        };
        const message = {
          notInterested: [event],
        };
        this.removeEventFromEventsList(event);
        sendSocket(message);
      }
    },

    /**
     * payload: { runnerIdList }
     */
    async subscribeMarkets(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload.marketIdList)) {
        let markets = [];
        payload.marketIdList.forEach(marketId => {
          if (marketId != null) {
            markets.push({
              type: "market",
              id: marketId,
            });
          }
        });

        if (markets.length > 0) {
          const message = {
            interested: markets,
          };
          markets.length > 0 &&
            markets.map(market => this.addMarketToMarketsList(market));
          sendSocket(message);
        }
      }
    },

    /**
     * payload: { marketIdList }
     */
    async unsubscribeMarkets(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload.marketIdList)) {
        let markets = [];
        payload.marketIdList.forEach(marketId => {
          if (marketId != null) {
            markets.push({
              type: "market",
              id: marketId,
            });
          }
        });

        if (markets.length > 0) {
          const message = {
            notInterested: markets,
          };
          markets.length > 0 &&
            markets.map(market => this.removeMarketFromMarketsList(market));
          sendSocket(message);
        }
      }
    },

    /**
     * payload: { market } BET-3797
     */
    async subscribeFailedBetMarket(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        const market = {
          type: "market",
          id: payload.marketId,
          message: `failed bet ${payload.betId}`,
        };
        const message = {
          interested: [market],
        };
        sendSocket(message);
      }
    },

    /**
     * payload: { runner }
     */
    async subscribeRunner(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        const runner = {
          type: "runner",
          id: payload.runnerId,
        };
        const message = {
          interested: [runner],
        };
        this.addRunnerToRunnersList(runner);
        sendSocket(message);
      }
    },

    /**
     * payload: { runner } BET-3797
     */
    async subscribeFailedBetRunner(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        const runner = {
          type: "runner",
          id: payload.runnerId,
          message: `failed bet ${payload.betId}`,
        };
        const message = {
          interested: [runner],
        };
        sendSocket(message);
      }
    },

    /**
     * payload: { runner }
     */
    async unsubscribeRunner(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (!isEmpty(payload)) {
        if (!isEmpty(payload)) {
          const runner = {
            type: "runner",
            id: payload.runnerId,
          };
          const message = {
            notInterested: [runner],
          };
          this.removeRunnerFromRunnersList(runner);
          sendSocket(message);
        }
      }
    },

    /**
     * payload: { event, market or runner }
     * If web socket drops resubscribe stored events, markets and runner using stored values
     */
    async resubscribeEventsMarketAndRunners(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      // resubscribe events, markets and runners
      if (!isEmpty(rootState.subscribeModel.events)) {
        const events = rootState.subscribeModel.events.map(event => {
          return {
            type: "event",
            id: event,
          };
        });
        sendSocket({
          interested: events,
        });
      }

      if (!isEmpty(rootState.subscribeModel.markets)) {
        const markets = rootState.subscribeModel.markets.map(market => {
          return {
            type: "market",
            id: market,
          };
        });
        sendSocket({
          interested: markets,
        });
      }

      if (!isEmpty(rootState.subscribeModel.runners)) {
        const runners = rootState.subscribeModel.runners.map(runner => {
          return {
            type: "runner",
            id: runner,
          };
        });
        sendSocket({
          interested: runners,
        });
      }
    },
  },
};
export default SubscribeModel;
